/**
 * ItemFocusView — sidebar drill-in for "looking at" an item worn by
 * another character.
 *
 * Renders the same content body as ``ItemDetailPanel`` (image, name,
 * quality tier badge, markdown description, stats grid, facet chips) but
 * inline rather than inside a Sheet drawer. Read-only except for one
 * affordance: a Steal button that renders ONLY when the backend's
 * ``can_steal`` flag is true (visibility = eligibility, #1909) — the
 * looker doesn't own the item, but may still be allowed to take it.
 *
 * Data is fetched via ``useVisibleItemDetail``; the backend endpoint
 * already enforces the visibility rules (own / same-room / staff bypass)
 * so concealed items return 404 and we render an "unavailable" state.
 */

import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Hand, HandMetal } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useActionResult } from '@/hooks/actionResultBus';
import type { ActionResultPayload } from '@/hooks/types';
import { useGameSocket } from '@/hooks/useGameSocket';
import type { MyRosterEntry } from '@/roster/types';

import { RankingBoardCard } from '@/renown/components/RankingBoardCard';
import { inventoryKeys } from '../hooks/useInventory';
import { useVisibleItemDetail } from '../hooks/useVisibleItemDetail';
import { visibleWornKeys } from '../hooks/useVisibleWornItems';
import type { ItemInstance } from '../types';

interface ItemFocusViewProps {
  item: { id: number; name: string };
  /**
   * The id of the requester's currently active character — passed to the
   * backend as the ``observer`` query parameter so visibility rules
   * (same room / self-look / staff) can be enforced. ``null`` when no
   * puppet is active; the detail fetch is then disabled and the
   * "unavailable" state renders. Doubles as "my own character id" for
   * invalidating my inventory after a successful steal.
   */
  observerId: number | null;
  /** Active puppet name — required to dispatch Steal; the button is
   * inert without it. */
  character?: MyRosterEntry['name'] | null;
  /** Called after a successful steal (e.g. to pop the focus stack back to
   * the character view, since the item is no longer theirs to look at). */
  onStolen?: () => void;
  className?: string;
}

export function ItemFocusView({
  item,
  observerId,
  character = null,
  onStolen,
  className,
}: ItemFocusViewProps) {
  const { data, isLoading, isError } = useVisibleItemDetail(item.id, observerId ?? undefined);
  const { executeAction } = useGameSocket();
  const queryClient = useQueryClient();

  // The action-result bus carries no action key (#1909 limitation shared
  // with WardrobePage's own broad invalidation) — scope the handler to
  // results that follow a steal *we* dispatched, so an unrelated action
  // succeeding elsewhere doesn't pop this view or invalidate caches.
  const [stealPending, setStealPending] = useState(false);
  const handleActionResult = useCallback(
    (payload: ActionResultPayload) => {
      if (!stealPending) return;
      setStealPending(false);
      if (!payload.success) {
        toast.error(payload.message ?? 'Failed to steal.');
        return;
      }
      queryClient.invalidateQueries({ queryKey: visibleWornKeys.all }).catch(() => {});
      if (observerId != null) {
        queryClient
          .invalidateQueries({ queryKey: inventoryKeys.inventory(observerId) })
          .catch(() => {});
      }
      onStolen?.();
    },
    [stealPending, queryClient, observerId, onStolen]
  );
  useActionResult(handleActionResult);

  const handleSteal = useCallback(() => {
    if (!character || data?.game_object_id == null) return;
    setStealPending(true);
    executeAction(character, 'steal', { target_id: data.game_object_id });
  }, [character, data, executeAction]);

  if (isLoading) {
    return (
      <div className={cn('flex flex-col gap-4 p-4', className)} data-testid="item-focus-loading">
        <Skeleton className="mx-auto aspect-square w-full max-w-[280px]" />
        <Skeleton className="h-7 w-3/4" />
        <Skeleton className="h-5 w-1/3" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className={cn('flex flex-col gap-2 p-4', className)}>
        <h2 className="text-xl font-bold">{item.name}</h2>
        <p className="text-sm italic text-muted-foreground">Item details unavailable.</p>
      </div>
    );
  }

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      <ItemFocusBody item={data} />
      {data.can_steal && (
        <div className="px-4">
          <Button size="sm" variant="outline" onClick={handleSteal} disabled={stealPending}>
            <HandMetal className="mr-1.5 h-3.5 w-3.5" />
            Steal
          </Button>
        </div>
      )}
      {/* #761: an in-world object may carry a diegetic ranking board
          (herald, plaque, Academy tablet) — renders only when it does. */}
      <div className="px-4 pb-4">
        <RankingBoardCard objectId={item.id} />
      </div>
    </div>
  );
}

interface ItemFocusBodyProps {
  item: ItemInstance;
  className?: string;
}

function ItemFocusBody({ item, className }: ItemFocusBodyProps) {
  const tier = item.quality_tier;
  const tierColor = tier?.color_hex || '';
  const tintedBackground = tierColor ? `${tierColor}20` : undefined;
  const initial = item.display_name.trim().charAt(0).toUpperCase() || '?';
  // The visible-item-detail endpoint uses ``ItemInstanceReadSerializer``,
  // which does not (yet) nest facet labels. Track as a follow-up if Phase A
  // exposes them — until then the chip block is hidden.
  const facetLabels: string[] = [];

  return (
    <div className={cn('flex flex-col', className)}>
      <div className="flex flex-col gap-4 p-4 pb-3">
        <div
          style={tierColor ? { borderLeftColor: tierColor } : undefined}
          className="mx-auto flex aspect-square w-full max-w-[280px] items-center justify-center overflow-hidden rounded-lg border border-l-[3px] bg-muted"
          data-testid="item-focus-image"
        >
          {item.display_image_url ? (
            <img
              src={item.display_image_url}
              alt={item.display_name}
              className="h-full w-full object-cover"
            />
          ) : (
            <span data-fallback-initial className="text-6xl font-bold text-muted-foreground">
              {initial}
            </span>
          )}
        </div>

        <div className="space-y-2">
          <h2 className="text-2xl font-bold">{item.display_name}</h2>
          {tier?.name && (
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                style={tintedBackground ? { backgroundColor: tintedBackground } : undefined}
                className="border-transparent"
              >
                {tier.name}
              </Badge>
            </div>
          )}
        </div>
      </div>

      <Separator />

      <div className="flex flex-col gap-5 p-4">
        {item.display_description && (
          <div
            className={cn('max-w-none text-base leading-relaxed', proseClasses)}
            data-testid="item-focus-description"
          >
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{item.display_description}</ReactMarkdown>
          </div>
        )}

        <StatsGrid item={item} />

        {facetLabels.length > 0 && (
          <div className="flex flex-col gap-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Facets
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {facetLabels.map((label) => (
                <span
                  key={label}
                  className="rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"
                >
                  {label}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface StatsGridProps {
  item: ItemInstance;
}

function StatsGrid({ item }: StatsGridProps) {
  const template = item.template;
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
      <Stat label="Weight" value={template?.weight ?? '-'} />
      <Stat label="Size" value={template ? String(template.size) : '-'} />
      <Stat label="Value" value={template ? String(template.value) : '-'} />
      {item.quantity > 1 && <Stat label="Quantity" value={String(item.quantity)} />}
      {item.charges > 0 && <Stat label="Charges" value={String(item.charges)} />}
    </div>
  );
}

interface StatProps {
  label: string;
  value: string;
}

function Stat({ label, value }: StatProps) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <Hand className="h-3 w-3 opacity-60" aria-hidden="true" />
        {label}
      </span>
      <span className="font-medium text-foreground">{value}</span>
    </div>
  );
}

/**
 * Manual editorial typography — `@tailwindcss/typography` is not installed.
 * Mirrors the prose classes used by ``ItemDetailPanel``.
 */
const proseClasses = [
  '[&_p]:my-3',
  '[&_p:first-child]:mt-0',
  '[&_p:last-child]:mb-0',
  '[&_strong]:font-semibold',
  '[&_em]:italic',
  '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:my-1',
  '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground',
  '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-sm',
  '[&_a]:text-primary [&_a]:underline-offset-2 hover:[&_a]:underline',
].join(' ');
